import { useCallback, useEffect, useState } from 'react';

// Server-backed Fresh Profiles state with a client-side fallback. Backend
// values take precedence, while local state keeps the UI responsive if the
// record-view request is briefly unavailable.
const STORAGE_KEY = 'searchjob.profile-freshness.v1';

const emptyScope = () => ({
  initialized: false,
  known: {},
  fresh: {},
  opened: {},
});

const readStore = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : { scopes: {} };
  } catch {
    return { scopes: {} };
  }
};

const writeStore = (store) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Local storage can be unavailable in private browsing. The in-memory
    // state still gives the current admin session the expected behaviour.
  }
};

const applicationKey = (candidate) => {
  const id = candidate?.application_id ?? candidate?.id;
  if (id !== null && id !== undefined && id !== '') return String(id);

  const email = candidate?.candidate_email || candidate?.email;
  return email ? String(email).trim().toLowerCase() : '';
};

const serverFreshness = (candidate) => {
  if (typeof candidate?.is_fresh_profile === 'boolean') {
    return candidate.is_fresh_profile;
  }
  if (typeof candidate?.is_profile_opened === 'boolean') {
    return !candidate.is_profile_opened;
  }
  if (candidate?.profile_first_viewed_at) return false;
  return null;
};

/**
 * Tracks Fresh Profiles using backend state, with a fallback for older API
 * responses during a rolling deployment.
 *
 * If the API does not yet send freshness fields, existing candidates are
 * baselined as seen on first load and later arrivals remain fresh until an
 * admin explicitly opens their profile.
 */
export default function useProfileFreshness(candidates, scope, { ready = true } = {}) {
  const [scopeState, setScopeState] = useState(emptyScope);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const store = readStore();
    const scopes = store.scopes && typeof store.scopes === 'object' ? store.scopes : {};
    const current = { ...emptyScope(), ...(scopes[scope] || {}) };
    current.known = { ...(current.known || {}) };
    current.fresh = { ...(current.fresh || {}) };
    current.opened = { ...(current.opened || {}) };

    const list = Array.isArray(candidates) ? candidates : [];

    if (!current.initialized && !ready) {
      // Wait for the first candidate request to settle. Otherwise every
      // existing profile would arrive after the initial empty render and be
      // incorrectly interpreted as newly received.
      return;
    }

    if (!current.initialized) {
      // We cannot know whether historical candidates were viewed before this
      // UI shipped, so establish them as the initial baseline instead of
      // presenting every existing record as newly received.
      list.forEach((candidate) => {
        const key = applicationKey(candidate);
        if (key) current.known[key] = true;
      });
      current.initialized = true;
    } else {
      list.forEach((candidate) => {
        const key = applicationKey(candidate);
        if (!key) return;

        const serverValue = serverFreshness(candidate);
        const isNewToThisBrowser = !current.known[key];
        current.known[key] = true;

        if (current.opened[key] || serverValue === false) {
          delete current.fresh[key];
        } else if (serverValue === true || (isNewToThisBrowser && serverValue === null)) {
          current.fresh[key] = true;
        }
      });
    }

    const nextStore = { ...store, scopes: { ...scopes, [scope]: current } };
    writeStore(nextStore);
    setScopeState(current);
  }, [candidates, ready, scope]);

  const isFreshProfile = useCallback(
    (candidate) => {
      const key = applicationKey(candidate);
      if (!key || scopeState.opened?.[key]) return false;

      const backendValue = serverFreshness(candidate);
      if (backendValue !== null) return backendValue;
      return Boolean(scopeState.fresh?.[key]);
    },
    [scopeState]
  );

  const markProfileOpened = useCallback(
    (candidate) => {
      const key = applicationKey(candidate);
      if (!key) return;

      setScopeState((previous) => {
        const next = {
          ...emptyScope(),
          ...previous,
          known: { ...(previous.known || {}), [key]: true },
          fresh: { ...(previous.fresh || {}) },
          opened: { ...(previous.opened || {}), [key]: new Date().toISOString() },
        };
        delete next.fresh[key];

        if (typeof window !== 'undefined') {
          const store = readStore();
          const scopes = store.scopes && typeof store.scopes === 'object' ? store.scopes : {};
          writeStore({ ...store, scopes: { ...scopes, [scope]: next } });
        }
        return next;
      });
    },
    [scope]
  );

  return { isFreshProfile, markProfileOpened };
}
